import { Component, h, Host, Element, State } from '@stencil/core';

import wcShared from '@web-components/shared';

import { setupTheme } from '../../utils/theme';

type ToolbarSection =
  | 'switch-user'
  | 'user-memos'
  | 'user-history'
  | 'recent-tickets'
  | 'locked-products';

@Component({
  tag: 'module-toolbar-autolog-component',
  styleUrl: 'wc-toolbar-autolog.scss',
  shadow: true,
})
export class ToolbarAutolog {
  @Element() hostElement: HTMLModuleToolbarAutologComponentElement;
  @State() isDragging = false;
  @State() activeSection: ToolbarSection | null = null;
  @State() theme: 'dark' | 'light' = 'light';

  private offsetX = 0;
  private offsetY = 0;
  private readonly POSITION_STORAGE_KEY = 'wc-toolbar-autolog-position';
  private cleanupTheme?: () => void;

  get currentTheme() {
    return this.theme;
  }

  componentDidLoad() {
    this.cleanupTheme = setupTheme(this);
    wcShared.displayComponent('toolbar-autolog');
    this.initializeDragging();
    this.loadPosition();
    this.addResizeListener();
  }

  disconnectedCallback() {
    this.cleanupTheme?.();
  }

  private initializeDragging() {
    this.hostElement.style.position = 'fixed';
    this.hostElement.style.zIndex = '9999';
  }

  private setToolbarPosition(x: number, y: number) {
    this.hostElement.style.left = `${x}px`;
    this.hostElement.style.top = `${y}px`;
    this.hostElement.style.right = 'auto';
  }

  private loadPosition() {
    const savedPosition = localStorage.getItem(this.POSITION_STORAGE_KEY);
    if (savedPosition) {
      const { x, y } = JSON.parse(savedPosition);
      const validatedPosition = this.validatePosition(x, y);
      this.setToolbarPosition(validatedPosition.x, validatedPosition.y);
    } else {
      // first render
      const rect = this.hostElement.getBoundingClientRect();
      const rightX = window.innerWidth - rect.width - 70;
      const topY = 110;
      this.setToolbarPosition(rightX, topY);
    }
  }

  private savePosition() {
    const rect = this.hostElement.getBoundingClientRect();
    const position = {
      x: rect.left,
      y: rect.top,
    };
    localStorage.setItem(this.POSITION_STORAGE_KEY, JSON.stringify(position));
  }

  private validatePosition(x: number, y: number) {
    const rect = this.hostElement.getBoundingClientRect();
    const toolbarWidth = rect.width;
    const toolbarHeight = rect.height;

    const maxX = window.innerWidth - toolbarWidth;
    const maxY = window.innerHeight - toolbarHeight;

    const validatedX = Math.max(0, Math.min(x, maxX));
    const validatedY = Math.max(0, Math.min(y, maxY));

    return { x: validatedX, y: validatedY };
  }

  private addResizeListener() {
    window.addEventListener('resize', this.handleWindowResize);
  }

  private handleWindowResize = () => {
    const rect = this.hostElement.getBoundingClientRect();

    if (rect.width === 0 || rect.height === 0) {
      return;
    }

    const validatedPosition = this.validatePosition(rect.left, rect.top);
    this.setToolbarPosition(validatedPosition.x, validatedPosition.y);
    this.savePosition();
  };

  private handleMouseDown = (event: MouseEvent) => {
    this.isDragging = true;

    const rect = this.hostElement.getBoundingClientRect();
    this.offsetX = event.clientX - rect.left;
    this.offsetY = event.clientY - rect.top;

    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);

    event.preventDefault();
    event.stopPropagation();
  };

  private handleMouseMove = (event: MouseEvent) => {
    if (!this.isDragging) return;

    const newX = event.clientX - this.offsetX;
    const newY = event.clientY - this.offsetY;
    const validatedPosition = this.validatePosition(newX, newY);
    this.setToolbarPosition(validatedPosition.x, validatedPosition.y);
  };

  private handleMouseUp = () => {
    this.isDragging = false;
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);

    this.savePosition();
  };

  private handleSectionClick = (section: ToolbarSection) => {
    this.activeSection = this.activeSection === section ? null : section;
  };

  private handleSectionClose = () => {
    this.activeSection = null;
  };

  private renderSectionContent() {
    if (!this.activeSection) return null;

    switch (this.activeSection) {
      case 'switch-user':
        return <switch-user-section onCloseSection={this.handleSectionClose}></switch-user-section>;
      case 'user-memos':
        return <user-memos-section onCloseSection={this.handleSectionClose}></user-memos-section>;
      case 'user-history':
        return (
          <user-history-section onCloseSection={this.handleSectionClose}></user-history-section>
        );
      case 'recent-tickets':
        return (
          <recent-tickets-section onCloseSection={this.handleSectionClose}></recent-tickets-section>
        );
      case 'locked-products':
        return (
          <locked-products-section
            onCloseSection={this.handleSectionClose}
          ></locked-products-section>
        );
      default:
        return null;
    }
  }

  render() {
    return (
      <Host class={{ [`theme-${this.currentTheme}`]: true, 'is-dragging': this.isDragging }}>
        <div class="wc-toolbar-autolog">
          <div class="module-toolbar">
            <div
              class="drag-handle"
              onMouseDown={this.handleMouseDown}
            >
              <wc-icon
                name="pin"
                size="20"
              ></wc-icon>
            </div>
            <button
              class={{
                'toolbar-section': true,
                active: this.activeSection === 'switch-user',
              }}
              onClick={() => this.handleSectionClick('switch-user')}
            >
              <wc-icon
                name="mattermost_account-multiple-outline"
                size="24"
              ></wc-icon>
              <span class="toolbar-label">Switch user</span>
            </button>
            <button
              class={{
                'toolbar-section': true,
                active: this.activeSection === 'user-memos',
              }}
              onClick={() => this.handleSectionClick('user-memos')}
            >
              <wc-icon
                name="file-stacked"
                size="24"
              ></wc-icon>
              <span class="toolbar-label">User memos</span>
            </button>
            <button
              class={{
                'toolbar-section': true,
                active: this.activeSection === 'user-history',
              }}
              onClick={() => this.handleSectionClick('user-history')}
            >
              <wc-icon
                name="time-clock"
                size="24"
              ></wc-icon>
              <span class="toolbar-label">User history</span>
            </button>
            <button
              class={{
                'toolbar-section': true,
                active: this.activeSection === 'recent-tickets',
              }}
              onClick={() => this.handleSectionClick('recent-tickets')}
            >
              <wc-icon
                name="envelope-search"
                size="24"
              ></wc-icon>
              <span class="toolbar-label">User tickets</span>
            </button>
            <button
              class={{
                'toolbar-section': true,
                active: this.activeSection === 'locked-products',
              }}
              onClick={() => this.handleSectionClick('locked-products')}
            >
              <wc-icon
                name="file-arrow-loop"
                size="24"
              ></wc-icon>
              <span class="toolbar-label">Locked products</span>
            </button>
          </div>
          {this.renderSectionContent()}
        </div>
      </Host>
    );
  }
}
